'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface FadeProps {
  children: React.ReactNode
  show?: boolean
  duration?: number
  className?: string
}

export function Fade({ 
  children, 
  show = true, 
  duration = 300, 
  className = '' 
}: FadeProps) {
  const [shouldRender, setShouldRender] = useState(show)
  const [opacity, setOpacity] = useState(show ? 1 : 0)

  useEffect(() => {
    if (show) {
      setShouldRender(true)
      // 等待下一帧再设置不透明度，以确保过渡效果
      const timer = setTimeout(() => {
        setOpacity(1)
      }, 10)
      return () => clearTimeout(timer)
    } else {
      setOpacity(0)
      const timer = setTimeout(() => {
        setShouldRender(false)
      }, duration)
      return () => clearTimeout(timer)
    }
  }, [show, duration])

  const style = {
    transition: `opacity ${duration}ms ease-in-out`,
    opacity: opacity
  }

  return shouldRender ? (
    <div style={style} className={className}>
      {children}
    </div>
  ) : null
}

interface SlideProps {
  children: React.ReactNode
  show?: boolean
  direction?: 'up' | 'down' | 'left' | 'right'
  duration?: number
  distance?: number
  className?: string
}

export function Slide({ 
  children, 
  show = true, 
  direction = 'up', 
  duration = 300, 
  distance = 20,
  className = '' 
}: SlideProps) {
  const [shouldRender, setShouldRender] = useState(show)
  const [isVisible, setIsVisible] = useState(show)

  useEffect(() => {
    if (show) {
      setShouldRender(true)
      // 等待下一帧再设置可见性，以确保过渡效果
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 10)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
      const timer = setTimeout(() => {
        setShouldRender(false)
      }, duration)
      return () => clearTimeout(timer)
    }
  }, [show, duration])

  const getTransform = () => {
    if (isVisible) return 'translate(0, 0)'
    
    switch (direction) {
      case 'up': return `translate(0, ${distance}px)`
      case 'down': return `translate(0, -${distance}px)`
      case 'left': return `translate(${distance}px, 0)`
      case 'right': return `translate(-${distance}px, 0)`
      default: return `translate(0, ${distance}px)`
    }
  }

  const style = {
    transition: `opacity ${duration}ms ease-in-out, transform ${duration}ms ease-in-out`,
    opacity: isVisible ? 1 : 0,
    transform: getTransform()
  }

  return shouldRender ? (
    <div style={style} className={className}>
      {children}
    </div>
  ) : null
}

interface TransitionProps {
  children: React.ReactNode;
  className?: string;
}

export function PageTransition({ children, className = '' }: TransitionProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function FadeTransition({ children, className = '' }: TransitionProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function SlideInTransition({ children, className = '' }: TransitionProps) {
  return (
    <motion.div
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 20, opacity: 0 }}
      transition={{ duration: 0.2 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

interface TransitionGroupProps {
  children: React.ReactNode;
  className?: string;
  show: boolean;
}

export function TransitionGroup({ children, className = '', show }: TransitionGroupProps) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function useTransitionState(initialState: boolean = false, delay: number = 300) {
  const [state, setState] = useState(initialState);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const toggle = () => {
    setIsTransitioning(true);
    setState(!state);
    setTimeout(() => {
      setIsTransitioning(false);
    }, delay);
  };

  return { state, isTransitioning, toggle, setState };
} 